import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { isError, linkEmail, startEmailSignIn, verifyEmailCode, type Session } from './api';

/**
 * Sign in with a code mailed to your address.
 *
 * Two steps on one screen rather than two screens, so the address stays visible while
 * the code is typed — the most common mistake is entering a code for an address you
 * mistyped, and being able to see it is what catches that.
 *
 * **There was a choice of channel here and F-103 removed it, along with the number field
 * and the radio group.** A code used to be deliverable to a WhatsApp number as well; with
 * the sidecar retired nothing can deliver one, and a channel that still accepted a number
 * would have taken it, said nothing (the API is forbidden from disclosing whether an
 * identity is registered), and left somebody watching a phone for a message that was never
 * sent. Leaving one channel is what makes this screen simpler than it was rather than
 * poorer: every account on the system signs in by address, including the one created by
 * number, which holds a proved address of its own.
 *
 * **There is no third step, and that is F-138.**
 *
 * Leo: *"After I sign in, it tells me to reload to start doing something. and it still gives
 * me dev focused copy 'reload and you should still be signed in'."* There used to be a `done`
 * stage — a panel saying there was nothing to do here yet and asking the learner to reload
 * the page. Both halves of that were notes to whoever built it: the reload was this screen
 * refusing to finish its own job, and "you should still be signed in" is a developer checking
 * a cookie, said out loud to somebody who only wanted to start learning.
 *
 * Signing in now ends with being signed in. The code is spent, the session is re-read, and
 * the route this screen is mounted on swaps it for the app — either the screen that was
 * guarded, or the landing that decides where somebody with no objective yet should be. This
 * component navigates nowhere itself: it is mounted in two places with two different right
 * answers, and a `navigate('/')` here would throw away the screen a learner had asked for.
 */
type Stage = 'address' | 'code';

export default function SignIn({
  onSignedIn,
}: {
  /**
   * Re-reads the session and **hands back what it read**, which is what the end of
   * `submitCode` decides on.
   *
   * It returns the session rather than nothing because the alternative was a check about
   * mount timing, and that check was wrong. It asked "am I still on screen?", on the
   * reasoning that re-reading the session is what unmounts this component — but `setSession`
   * schedules a default-lane update through React's scheduler, which is a macrotask, so this
   * function's `await` resolves *before* any re-render. The refusal below therefore ran on
   * every successful sign-in and was invisible only because the state update batched with
   * the parent's and the commit deleted this fiber before applying it. A React upgrade,
   * `StrictMode`, or one more `await` between `whoAmI()` and `setSession` would have put
   * "this browser did not keep the session" on the screen of everybody who just signed in.
   *
   * The session is a fact about the product and the render schedule is not, so the check is
   * about the fact.
   */
  onSignedIn: () => Promise<Session | undefined>;
}) {
  const { t } = useI18n();
  const [stage, setStage] = useState<Stage>('address');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const codeRef = useRef<HTMLInputElement>(null);

  /**
   * Arriving from a hand-off link that did not work (F-157).
   *
   * The API redirects here with `?link=expired` when a cross-device link was already spent or
   * had run out of time. **It says the same thing for both**, and so does this screen: which
   * of the two it was only ever tells whoever is holding the link whether a credential
   * existed, and there is nothing the learner does differently on hearing it. What they need
   * is the thing that always works — their address and a code.
   *
   * Read once, into state, and then taken out of the address bar below: reloading after they
   * have started typing should not put the notice back, and the query is about how they
   * arrived rather than about where they are.
   */
  const [linkExpired] = useState(
    () => new URLSearchParams(window.location.search).get('link') === 'expired',
  );

  useEffect(() => {
    if (!linkExpired) return;
    const here = new URL(window.location.href);
    here.searchParams.delete('link');
    window.history.replaceState(null, '', `${here.pathname}${here.search}${here.hash}`);
  }, [linkExpired]);

  // Focus follows the step, so a keyboard does not have to be dismissed and the field
  // re-tapped between entering an address and entering a code.
  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus();
  }, [stage]);

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await startEmailSignIn(email);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        result.error === 'invalid_email'
          ? t('signin.error.email')
          : result.error === 'rate_limited'
            ? t('signin.error.tooMany')
            : /*
               * A 503 means the service has no sender configured, which is a deploy that has
               * not happened rather than anything the person typed. It used to point at the
               * other channel; there is no other channel, so the copy now says plainly that
               * this is ours to fix and not theirs to work around. Naming a channel that does
               * not exist would send somebody looking for a field this screen no longer has.
               */
              result.status === 503
              ? t('signin.error.unconfigured')
              : t('signin.error.generic'),
      );
      return;
    }
    setStage('code');
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await verifyEmailCode(email, code);

    if (isError(result)) {
      setBusy(false);
      setProblem(t('signin.error.code'));
      setCode('');
      codeRef.current?.focus();
      return;
    }

    /*
     * Still busy across this await, on purpose: the button goes on saying "one moment"
     * until the app itself replaces this screen, rather than blinking back to a filled-in
     * form for the half second the session takes to come back.
     */
    const session = await onSignedIn();
    // Signed in: the route swaps this screen for the app, and there is nothing further
    // for this function to say. Left busy, so the button does not blink on the way out.
    if (session?.signedIn) return;

    /*
     * The code was spent and the session did not come back — a browser refusing our cookie,
     * or a network that dropped between the two requests. Neither is a mistake the learner
     * made and neither is something this screen can fix for them, so it says what happened
     * and names both, rather than blaming one.
     *
     * It is worth its own sentence because the silent version is this project's signature
     * failure: a sign-in that reports success and leaves somebody looking at a sign-in form
     * with nothing anywhere saying why.
     */
    setBusy(false);
    setProblem(t('signin.error.session'));
  }

  return (
    <section className="panel">
      <h2>{t('signin.title')}</h2>

      {/*
        Above the form and not inside it: it is not a refusal of anything they typed, it is an
        explanation of why they are looking at this screen at all. It gives way to `problem`,
        so a wrong code is never shown beside a notice about how they got here.
      */}
      {linkExpired && !problem && stage === 'address' && (
        <p className="problem" role="status">
          {t('signin.error.linkExpired')}
        </p>
      )}

      <form onSubmit={stage === 'address' ? requestCode : submitCode} className="form">
        <label className="field">
          <span>{t('signin.email.label')}</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            // Off, both of them. A phone that capitalises the first letter of every
            // field turns one address into two accounts as far as anybody typing can
            // tell — the API lowercases what it is given for exactly that reason, and
            // there is no sense in showing somebody a capital they did not intend.
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={stage !== 'address' || busy}
            required
          />
          <small>{t('signin.email.hint')}</small>
        </label>

        {stage === 'code' && (
          <label className="field">
            <span>{t('signin.code.label')}</span>
            <input
              ref={codeRef}
              type="text"
              // `one-time-code` is what makes a phone offer the code from the
              // notification instead of making somebody switch apps to read it.
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={busy}
              required
            />
            <small>{t('signin.code.hint')}</small>
          </label>
        )}

        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}

        <div className="actions">
          <button type="submit" className="button primary" disabled={busy}>
            {busy ? t('signin.working') : stage === 'address' ? t('email.submit') : t('signin.code.submit')}
          </button>

          {stage === 'code' && (
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => {
                setStage('address');
                setCode('');
                setProblem(undefined);
              }}
            >
              {t('signin.email.back')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

/**
 * Adding an email address to an account that already exists (F-101).
 *
 * In this file rather than its own because it is the same two steps as signing in —
 * prove an address, then spend the code — and the interesting part is what it is *not*
 * allowed to be. It calls `/api/auth/email/link`, which requires the session, and never
 * `/verify`: an endpoint that attached an address to whoever happened to be signed in
 * would mean a learner typing a second address to sign in with silently rewrote their
 * own account's, and one that attached it with no session at all would be a way to point
 * somebody else's account at an address you control.
 *
 * **The address the account already has is shown above this panel rather than inside it**
 * (F-140). This is the control for changing it, and the profile screen says which account
 * it is changing at the top of the same page — repeating it here would be the same fact
 * twice on one screen, with the second copy attached to a form that is about a different
 * address. What this panel still says on its own is the new one, after it is added, which
 * is the moment somebody needs to see it confirmed.
 *
 * `onLinked` is what keeps those two from disagreeing: linking an address changes which
 * address the account signs in with, so the session is re-read and the line above this panel
 * is the address that now works. Without it a learner would read the new address here and the
 * old one at the top of the same screen, which is precisely the confusion F-140 exists to end.
 */
export function EmailAccess({ onLinked }: { onLinked: () => void }) {
  const { t } = useI18n();
  const [stage, setStage] = useState<'idle' | 'address' | 'code' | 'done'>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus();
  }, [stage]);

  function reset() {
    setStage('idle');
    setEmail('');
    setCode('');
    setProblem(undefined);
  }

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await startEmailSignIn(email);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        result.error === 'invalid_email'
          ? t('email.error.email')
          : result.error === 'rate_limited'
            ? t('email.error.tooMany')
            : t('email.error.generic'),
      );
      return;
    }
    setStage('code');
  }

  async function confirm(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await linkEmail(email, code);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        // 409 is the one refusal worth its own sentence: the code was right and the
        // address belongs to another account, which is a different thing to do about it.
        result.error === 'in_use'
          ? t('email.error.inUse')
          : result.error === 'invalid_email'
            ? t('email.error.email')
            : t('email.error.code'),
      );
      setCode('');
      codeRef.current?.focus();
      return;
    }
    setStage('done');
    onLinked();
  }

  if (stage === 'done') {
    return (
      <section className="panel">
        <h2>{t('email.title')}</h2>
        <p className="muted" role="status">
          {t('email.done').replace('{email}', email)}
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>{t('email.title')}</h2>
      <p className="muted">{t('email.body')}</p>

      {stage === 'idle' ? (
        <div className="actions">
          <button type="button" className="button" onClick={() => setStage('address')}>
            {t('email.submit')}
          </button>
        </div>
      ) : (
        <form onSubmit={stage === 'address' ? requestCode : confirm} className="form">
          <label className="field">
            <span>{t('email.label')}</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={stage !== 'address' || busy}
              required
            />
          </label>

          {stage === 'code' && (
            <label className="field">
              <span>{t('email.code.label')}</span>
              <input
                ref={codeRef}
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                name="code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={busy}
                required
              />
              <small>{t('email.code.hint')}</small>
            </label>
          )}

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          <div className="actions">
            <button type="submit" className="button primary" disabled={busy}>
              {busy
                ? t('signin.working')
                : stage === 'address'
                  ? t('email.submit')
                  : t('email.confirm')}
            </button>
            <button type="button" className="button" disabled={busy} onClick={reset}>
              {t('email.cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
